"use client";

import React from "react";
import { ComponentConfig } from "@puckeditor/core";
import Link from "next/link";
import { ImagePicker } from "@/components/ImagePicker";
import { resolveMediaUrl } from "@/lib/resolve-media-url";

export interface AboutContentProps {
  heading?: string;
  intro?: string;
  storyHeading?: string;
  storyBody?: string;
  missionHeading?: string;
  missionBody?: string;
  facilityImage?: string;
  facilityImageAlt?: string;
  qualityImage?: string;
  qualityImageAlt?: string;
  certificationLabel?: string;
  ctaText?: string;
  ctaLink?: string;
}

export const AboutContentBlock: ComponentConfig<AboutContentProps> = {
  label: "About Page Content",
  fields: {
    heading: { type: "text", label: "Page Heading" },
    intro: { type: "textarea", label: "Intro Lead Text" },
    storyHeading: { type: "text", label: "Facility Story Heading" },
    storyBody: { type: "textarea", label: "Facility Story Body" },
    missionHeading: { type: "text", label: "Mission Heading" },
    missionBody: { type: "textarea", label: "Mission Body" },
    facilityImage: {
      type: "custom",
      label: "Facility Image",
      render: ({ value, onChange }: any) => <ImagePicker value={value} onChange={onChange} />,
    },
    facilityImageAlt: { type: "text", label: "Facility Image Description" },
    qualityImage: {
      type: "custom",
      label: "Quality Image",
      render: ({ value, onChange }: any) => <ImagePicker value={value} onChange={onChange} />,
    },
    qualityImageAlt: { type: "text", label: "Quality Image Description" },
    certificationLabel: { type: "text", label: "Facility Trust Label" },
    ctaText: { type: "text", label: "Button Text" },
    ctaLink: { type: "text", label: "Button Link" },
  },
  defaultProps: {
    heading: "Precision Cosmeceutical Chemistry & Manufacturing",
    intro: "We bridge the gap between high-potency active ingredient science and reliable, low-MOQ contract manufacturing for ambitious beauty brands.",
    storyHeading: "Our Facility & Philosophy",
    storyBody: "Shaaira Biocare was established to give growing brands a clearer path from formulation brief to approved commercial batch. Our workflow supports agile development, controlled scale-up, documented manufacturing, packaging coordination, and agreed quality checks without forcing every launch into a large initial run.",
    missionHeading: "Our Quality Commitment",
    missionBody: "To provide growing skin, hair, and personal-care brands with specification-led formulation, transparent sourcing decisions, documented quality checks, and production timelines confirmed against the approved scope.",
    facilityImage: "/og-lab-v2.jpg",
    facilityImageAlt: "Cosmetic formulation workflow in a bright laboratory",
    qualityImage: "/assets/categories/kits-v2.jpg",
    qualityImageAlt: "Unbranded private-label sample kit and packaging formats",
    certificationLabel: "ISO 9001:2015 & GMP-Aligned",
    ctaText: "Schedule Factory Audit Inquiry",
    ctaLink: "/contact",
  },
  render: ({
    heading,
    intro,
    storyHeading,
    storyBody,
    missionHeading,
    missionBody,
    facilityImage,
    facilityImageAlt,
    qualityImage,
    qualityImageAlt,
    certificationLabel,
    ctaText,
    ctaLink,
  }) => {
    return (
      <div>
        <section className="page-hero">
          <div className="container">
            <h1>{heading}</h1>
            <p className="lead">{intro}</p>
          </div>
        </section>

        <section className="sec sec-alt">
          <div className="container">
            <div className="about-split">
              <div className="col">
                <h2>{storyHeading}</h2>
                <p>{storyBody}</p>
              </div>
              <div className="col">
                <div className="about-img-card">
                  <img src={resolveMediaUrl(facilityImage, "/og-lab-v2.jpg")} alt={facilityImageAlt || "Cosmetic formulation workflow in a bright laboratory"} />
                  <div className="about-img-badge">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    {certificationLabel}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="sec">
          <div className="container">
            <div className="about-split">
              <div className="col">
                <div className="about-img-card">
                  <img src={resolveMediaUrl(qualityImage, "/assets/categories/kits-v2.jpg")} alt={qualityImageAlt || "Unbranded private-label sample kit and packaging formats"} />
                </div>
              </div>
              <div className="col">
                <h2>{missionHeading}</h2>
                <p>{missionBody}</p>
                <div style={{ marginTop: 24 }}>
                  <Link className="btn btn-primary" href={ctaLink || "/contact"}>
                    {ctaText || "Schedule Factory Audit Inquiry"}{" "}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  },
};
